import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangePasswordSchema } from "../../FormsValidations/Validations";
import { Alert } from "@mui/material";
import Typography from "../../Utility/Typography";
import Label from "../../Utility/Label/Label";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import PasswordInput from "../../Utility/PasswordToggle/PassswordInput";
import ErrorMsg from "../../Utility/ErrorMsg";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import { useLocation, useNavigate } from "react-router-dom";
import { post } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { logger } from "../../utils/logger";

const PASSWORD_RULES_HEADING =
  "Use 8+ characters with uppercase, lowercase, number, and special character";

const ChangePassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const prevPath = location.state?.previousPath || "/dashboard";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ChangePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [success, setSuccess] = useState(false);

  const onSubmit = async (data) => {
    try {
      setLocalError("");
      setLoading(true);
      await post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      logger.error("Change password failed:", err);
      if (typeof err === "object") {
        if (err?.response?.data?.message) {
          setLocalError(err.response.data.message);
        } else if (err?.message) {
          setLocalError(err.message);
        } else {
          setLocalError("Something went wrong. Please try again.");
        }
      } else {
        setLocalError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        <div className="d-flex align-items-center gap-2 mb-4">
          <Typography variant="h4" className="text-theme-primary mb-0">
            <BackArrow to={prevPath} /> Change Password
          </Typography>
        </div>
        <Typography variant="text2" className="text-text-secondary mb-4 block">
          {PASSWORD_RULES_HEADING}
        </Typography>

        <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md border border-border-primary shadow-sm">
          <div className="flex-1 p-6">
            {localError && (
              <Alert className="mb-4" severity="error">
                {localError}
              </Alert>
            )}
            {success && (
              <Alert className="mb-4" severity="success">
                Your password has been changed successfully.
              </Alert>
            )}
            {!success && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div
                    className={`rounded-sm ${
                      errors.currentPassword ? "border border-badge-danger" : ""
                    }`}
                  >
                    <PasswordInput
                      register={register}
                      name="currentPassword"
                      id="currentPassword"
                      apiErrors={null}
                      className={
                        errors.currentPassword ? "border-badge-danger" : "border-border-primary"
                      }
                      autoComplete="current-password"
                    />
                  </div>
                  <ErrorMsg message={errors.currentPassword?.message} className="mt-1" />
                </div>

                <div className="mb-4">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div
                    className={`rounded-sm ${
                      errors.newPassword ? "border border-badge-danger" : ""
                    }`}
                  >
                    <PasswordInput
                      register={register}
                      name="newPassword"
                      id="newPassword"
                      apiErrors={null}
                      className={
                        errors.newPassword ? "border-badge-danger" : "border-border-primary"
                      }
                      autoComplete="new-password"
                    />
                  </div>
                  <ErrorMsg message={errors.newPassword?.message} className="mt-1" />
                </div>

                <div className="mb-4">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div
                    className={`rounded-sm ${
                      errors.confirmPassword ? "border border-badge-danger" : ""
                    }`}
                  >
                    <PasswordInput
                      register={register}
                      name="confirmPassword"
                      id="confirmPassword"
                      apiErrors={null}
                      className={
                        errors.confirmPassword ? "border-badge-danger" : "border-border-primary"
                      }
                      autoComplete="new-password"
                    />
                  </div>
                  <ErrorMsg message={errors.confirmPassword?.message} className="mt-1" />
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    type="button"
                    variant="danger"
                    size="md"
                    disabled={loading}
                    onClick={() => navigate(prevPath)}
                    className="btn-reset"
                  >
                    Cancel
                  </Button>
                  <FormSubmitButton
                    loading={loading}
                    label="Update Password"
                    loadingText="Updating..."
                    idleIconClass=""
                    className="flex items-center justify-center gap-2"
                  />
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
